import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { CloseIcon } from "stream-chat-react";
import moment from "moment-timezone";
import { withoutEmojis } from "../../utils/emoji";
/**
 * TopUpCreditsModal
 * Two modes:
 *
 *  mode="topUp"   - Standalone "Top up credits" modal (image 10).
 *                    Shown from Settings / the header credit pill.
 *
 *  mode="paySend" - Combined "Send for Interpretation" + credit purchase
 *                    flow (image 12), shown instead of SendInterpretationDialog
 *                    when the org's balance is 0 and a report needs sending.
 *
 * Props:
 *  - open, onClose
 *  - mode: "topUp" | "paySend"
 *  - currentBalance: number
 *  - pricePerCredit: number (default 49)
 *  - quantityOptions: number[] (default [1, 2, 3, 5, 10])
 *  - report: { id, collection_id, user_data, createdAt } — required for paySend
 *  - recipientLabel: string — pre-selected recipient name, e.g. "Spandan MD Doctor"
 *  - onPayTopUp: (quantity) => void        // mode="topUp"
 *  - onPayAndSend: (quantity, notes) => void // mode="paySend"
 *  - loading: boolean
 */

const QuantitySelector = ({
  quantityOptions,
  quantity,
  setQuantity,
  pricePerCredit,
}) => {
  // Local text mirror of `quantity` so the field can be freely cleared/retyped
  // while editing, instead of snapping back on every keystroke.
  const [inputValue, setInputValue] = useState(String(quantity));

  useEffect(() => {
    setInputValue(String(quantity));
  }, [quantity]);

  const commitInputValue = () => {
    const parsed = parseInt(inputValue, 10);
    if (Number.isInteger(parsed) && parsed > 0) {
      setQuantity(parsed);
    } else {
      setInputValue(String(quantity));
    }
  };

  return (
  <>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: "6px", sm: "8px" }, marginBottom: "12px" }}>
      {quantityOptions.map((q) => (
        <Box
          key={q}
          onClick={() => setQuantity(q)}
          sx={{
            width: { xs: "32px", sm: "36px" },
            height: { xs: "32px", sm: "36px" },
            flexShrink: 0,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: { xs: "13px", sm: "14px" },
            fontWeight: 600,
            cursor: "pointer",
            backgroundColor: quantity === q ? "#6c45ff" : "#fff",
            color: quantity === q ? "#fff" : "#4c4a5c",
            border: quantity === q ? "none" : "1px solid #deddea",

            "&:hover": {
              borderColor: quantity === q ? "none" : "#c9c5dd",
            },
          }}
        >
          {q}
        </Box>
      ))}
    </Box>

    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        marginBottom: "6px",
      }}
    >
      <IconButton
        size="small"
        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
        sx={{
          border: "1px solid #deddea",
          borderRadius: "7px",
          width: "32px",
          height: "32px",
        }}
      >
        −
      </IconButton>

      <TextField
        value={inputValue}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "" || /^[0-9]+$/.test(raw)) setInputValue(raw);
        }}
        onBlur={commitInputValue}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.target.blur();
        }}
        inputProps={{
          inputMode: "numeric",
          style: { textAlign: "center", padding: "6px 4px", fontSize: "16px", fontWeight: 600 },
        }}
        sx={{
          width: "56px",
          "& .MuiOutlinedInput-root": { borderRadius: "7px" },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#deddea" },
        }}
        size="small"
      />

      <IconButton
        size="small"
        onClick={() => setQuantity((q) => q + 1)}
        sx={{
          border: "1px solid #deddea",
          borderRadius: "7px",
          width: "32px",
          height: "32px",
        }}
      >
        +
      </IconButton>
    </Box>

    <Typography sx={{ fontSize: "13px", color: "#77758a" }}>
      ₹{pricePerCredit} × {quantity}
    </Typography>
  </>
  );
};

const TopUpCreditsModal = ({
  open,
  onClose,
  mode = "topUp",
  currentBalance = 0,
  pricePerCredit = 49,
  quantityOptions = [1, 2, 3, 5, 10],
  report,
  recipientLabel = "Spandan MD Doctor",
  onPayTopUp,
  onPayAndSend,
  loading = false,
}) => {
  const [quantity, setQuantity] = useState(2);
  const [notes, setNotes] = useState("");

  const totalAmount = pricePerCredit * quantity;

  const handlePrimaryAction = () => {
    if (mode === "topUp") {
      onPayTopUp && onPayTopUp(quantity);
    } else {
      onPayAndSend && onPayAndSend(quantity, notes);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "475px",
          maxWidth: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)" },
          maxHeight: { xs: "calc(100% - 16px)", sm: "calc(100% - 64px)" },
          borderRadius: "14px",
          padding: { xs: "4px 14px 10px", sm: "6px 20px 14px" },
          margin: { xs: "8px", sm: "16px" },
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.18)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: { xs: "6px 0 10px", sm: "8px 0 12px" },
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: { xs: "16px", sm: "18px" }, fontWeight: 600, color: "#22223b" }}>
          {mode === "topUp" ? "Top up credits" : "Send for Interpretation"}
        </Typography>

        <IconButton onClick={onClose} size="small" sx={{ color: "#777", padding: "4px" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          padding: "0 !important",
          overflowY: "auto",
          overflowX: "hidden",
          flex: "1 1 auto",
          minHeight: 0,
        }}
      >
        {mode === "topUp" && (
          <Typography sx={{ fontSize: "13px", color: "#77758a", marginBottom: "16px" }}>
            Interpretation credits are used to send ECG reports for expert
            interpretation.
          </Typography>
        )}

        {/* Report + recipient info — paySend mode only */}
        {mode === "paySend" && report && (
          <>
            <Box
              sx={{
                backgroundColor: "#f8f7fc",
                border: "1px solid #e2e0ed",
                borderRadius: "11px",
                padding: "14px",
                marginBottom: "16px",
              }}
            >
              <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#24243b", marginBottom: "4px" }}>
                #{report?.collection_id} |{" "}
                <span style={{ fontWeight: 600 }}>
                  {report?.user_data?.first_name} {report?.user_data?.last_name}
                </span>
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#69677d", marginBottom: "3px" }}>
                {report?.user_data?.userAge} years | {report?.user_data?.gender}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#77758a" }}>
                {moment(report?.createdAt).format("D MMM YYYY, h:mm a")}
              </Typography>
            </Box>

            <Box sx={{ marginBottom: "14px" }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#66647a", marginBottom: "6px" }}>
                Send to
              </Typography>
              <Box
                sx={{
                  height: "44px",
                  borderRadius: "9px",
                  border: "1px solid #deddea",
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 13px",
                  fontSize: "14px",
                  color: "#29283d",
                }}
              >
                {recipientLabel}
              </Box>
            </Box>

            <Box sx={{ marginBottom: "14px" }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#66647a", marginBottom: "6px" }}>
                Notes
              </Typography>
              <TextField
                multiline
                rows={2}
                fullWidth
                placeholder="Anything the doctor should know"
                value={notes}
                onChange={withoutEmojis((e) => setNotes(e.target.value))}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "9px",
                    fontSize: "14px",
                    backgroundColor: "#fff",
                  },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#deddea" },
                }}
              />
            </Box>
          </>
        )}

        {/* Credit balance + quantity selector */}
        <Box
          sx={{
            backgroundColor: mode === "paySend" ? "#f8f7fc" : "transparent",
            border: mode === "paySend" ? "1px solid #e2e0ed" : "none",
            borderRadius: "11px",
            padding: mode === "paySend" ? "14px" : "0",
            marginBottom: "16px",
          }}
        >
          {mode === "topUp" ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "14px",
              }}
            >
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#66647a" }}>
                Current balance
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                {/* <Box
                  sx={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    backgroundColor: "#7651ff",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    fontWeight: 700,
                  }}
                >
                  ₹
                </Box> */}
                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#22223b" }}>
                  {currentBalance} credits
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ marginBottom: "12px" }}>
              <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#22223b" }}>
                You have {currentBalance} credits
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#77758a" }}>
                Add credits to send this report — 1 credit is needed.
              </Typography>
            </Box>
          )}

          <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#66647a", marginBottom: "8px" }}>
            Select quantity
          </Typography>

          <QuantitySelector
            quantityOptions={quantityOptions}
            quantity={quantity}
            setQuantity={setQuantity}
            pricePerCredit={pricePerCredit}
          />
        </Box>

        {/* Total */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#eeeaff",
            border: "1px solid #ddd4ff",
            borderRadius: "9px",
            padding: "12px 14px",
            marginBottom: "10px",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#22223b" }}>
              {quantity} credit{quantity === 1 ? "" : "s"}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#6348cc" }}>
              ₹{pricePerCredit} per credit
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#22223b" }}>
            ₹{totalAmount}
          </Typography>
        </Box>

        {mode === "paySend" && (
          <Typography sx={{ fontSize: "12px", color: "#77758a", marginBottom: "14px", lineHeight: 1.5 }}>
            1 credit is used now to send this report. Any extra credits are
            added to your balance.
          </Typography>
        )}

        <Button
          fullWidth
          variant="contained"
          disabled={loading}
          onClick={handlePrimaryAction}
          sx={{
            height: "44px",
            borderRadius: "9px",
            backgroundColor: "#6c45ff",
            color: "#fff",
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 600,
            boxShadow: "none",
            marginBottom: mode === "topUp" ? 0 : "14px",

            "&:hover": { backgroundColor: "#5d38eb", boxShadow: "none" },
            "&:disabled": { backgroundColor: "#b5a5ff", color: "#fff" },
          }}
        >
          {loading
            ? mode === "topUp"
              ? "Processing..."
              : "Sending..."
            : mode === "topUp"
              ? `Pay ₹${totalAmount} →`
              : `Pay ₹${totalAmount} & Send`}
        </Button>

        {mode === "topUp" && (
          <Typography sx={{ fontSize: "12px", color: "#999", textAlign: "center", marginTop: "10px" }}>
            🔒 Secure payment · powered by your saved payment method
          </Typography>
        )}

        {mode === "paySend" && (
          <Typography sx={{ fontSize: "12px", color: "#716e83" }}>
            You can auto-send all reports to Spandan Expert from{" "}
            <span style={{ color: "#6744ff", fontWeight: 600 }}>Settings</span>
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TopUpCreditsModal;